import React, {useCallback, useEffect, useRef} from 'react';
import {observer} from 'mobx-react';
import ReactQuill, {Quill} from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import s from '../../s';
import {DefaultPCS, EPMethod} from '../../rendering/render.def';
import {FONT_OPTIONS, FONT_FAMILY_MAP} from './ep-extended/engraved-plate-extended.store';

const Block = Quill.import('blots/block');
const Inline = Quill.import('blots/inline');

class ColorStyle extends Inline {
    static create(value) {
        const node = super.create();

        node.style.color = value;

        return node;
    }

    static formats(node) {
        return node.style.color;
    }
}

class BackgroundStyle extends Inline {
    static create(value) {
        const node = super.create();

        node.style.backgroundColor = value;

        return node;
    }

    static formats(node) {
        return node.style.backgroundColor;
    }
}

class IndentStyle extends Block {
    static create(value) {
        const node = super.create();

        value = value > 0 ? value : 0;
        node.style.marginLeft = `${value * 0.75}em`;

        return node;
    }

    static formats(node) {
        const margin = node.style.marginLeft || '0';

        return parseFloat(margin) / 0.75;
    }
}

class SizeStyle extends Inline {
    static create(value) {
        const node = super.create();
        const sizeMap = {
            small: '0.75em',
            normal: '1em',
            large: '1.5em',
            huge: '2em',
        };

        node.style.fontSize = sizeMap[value] || value;

        return node;
    }

    static formats(node) {
        return node.style.fontSize;
    }
}

class FontStyle extends Inline {
    static create(value) {
        const node = super.create();

        node.style.fontFamily = value;

        return node;
    }

    static formats(node) {
        return node.style.fontFamily;
    }
}

class AlignStyle extends Block {
    static create(value) {
        const node = super.create();

        node.style.textAlign = value;

        return node;
    }

    static formats(node) {
        return node.style.textAlign || 'left';
    }
}

AlignStyle.blotName = 'align';
AlignStyle.tagName = 'p';
Quill.register(AlignStyle, true);

FontStyle.blotName = 'font';
FontStyle.tagName = 'span';
Quill.register(FontStyle, true);

SizeStyle.blotName = 'size';
SizeStyle.tagName = 'span';
Quill.register(SizeStyle, true);

IndentStyle.blotName = 'indent';
IndentStyle.tagName = 'p';
Quill.register(IndentStyle, true);

ColorStyle.blotName = 'color';
ColorStyle.tagName = 'span';
Quill.register(ColorStyle, true);

BackgroundStyle.blotName = 'background';
BackgroundStyle.tagName = 'span';
Quill.register(BackgroundStyle, true);

const Font = Quill.import('formats/font');

Font.whitelist = FONT_OPTIONS.map(f => f.label);
Quill.register(Font, true);

const modules = {
    toolbar: [[{font: FONT_OPTIONS.map(f => f.label)}], [{size: ['small', false, 'large', 'huge']}], ['bold', 'italic', 'underline', 'strike'], [{color: []}, {background: []}], [{list: 'ordered'}, {list: 'bullet'}], [{indent: '-1'}, {indent: '+1'}], [{align: []}], ['clean']],
};

const formats = ['header', 'font', 'size', 'bold', 'italic', 'underline', 'strike', 'color', 'background', 'list', 'bullet', 'indent', 'align'];

export const EPEditor = observer(() => {
    const quillRef = useRef<ReactQuill | null>(null);

    useEffect(() => {
        const styleId = 'quill-custom-fonts';

        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');

            style.id = styleId;
            style.innerHTML = `${FONT_OPTIONS.map(font => {
                const fontFamily = FONT_FAMILY_MAP[font.value];

                return `.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="${font.label}"]::before,
                        .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="${font.label}"]::before {
                            content: "${font.label}";
                            font-family: ${fontFamily};
                        }
                        .ql-font-${font.label} {
                            font-family: ${fontFamily};
                        }`;
            }).join('\n')}
                .ql-editor {
                    font-family: ${FONT_FAMILY_MAP[1]};
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                }
            `;

            document.head.appendChild(style);
        }

        if (quillRef.current) {
            const editor = quillRef.current.getEditor();

            editor.format('font', 'Roboto');
            editor.format('align', 'center');
        }

        setHeight();
        setContainerStyle();
        render();
    }, []);

    const setContainerStyle = useCallback(() => {
        const container = document.querySelector('.ql-container') as HTMLElement;
        const editorElement = document.querySelector('.ql-editor') as HTMLElement;

        container.style.width = `calc(${s.pcs.css.ep_width} + 6px)`;

        if (s.pcs.EPMethodID === EPMethod.SingleDoc) {
            container.style.height = `calc(${s.pcs.css.ep_height} + 6px)`;
            container.style.overflow = 'hidden';

            if (editorElement) {
                editorElement.style.overflow = 'hidden';
                editorElement.style.height = '100%';
            }
        }
    }, []);

    const setValue = useCallback((value: string) => {
        setHeight();
        s.ps.EPContent = value;
        render();
    }, []);

    const setHeight = useCallback(() => {
        if (s.pcs.EPMethodID === EPMethod.Custom) {
            if (!quillRef.current) return;

            const editor = quillRef.current.getEditor();
            const editorRoot = editor.root;
            const height = editorRoot.scrollHeight;
            const heightIn = height / s.pcs.DPI;
            let extra = Math.floor(heightIn / 2) * 0.15;

            if (heightIn > 6) {
                extra -= 0.05;
            }

            const computedHeight = heightIn + extra;
            DefaultPCS.DefaultEPHeight = computedHeight;
            s.ps.EPHeight = computedHeight;
        }
    }, []);

    const render = useCallback(() => {
        s.ui.doRender = true;
        s.ui.update_pricing = true;
        s.pulse();
    }, []);

    return (
        <ReactQuill
            theme="snow"
            ref={quillRef}
            onChange={setValue}
            modules={modules}
            formats={formats}
        />
    );
});
